import React, {
  useEffect,
  useState,
  useContext,
  useMemo,
  useCallback,
} from 'react';

import { Box, Button, Stack, Typography } from '@mui/material';
import { Add } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styleColors from '../colors.styles';
import TemplateItem from './TemplateItem';
import SpinningSpark from '../shared/components/SpinningSpark';
import TemplatesFilters from './TemplatesFilters';
import {
  debounce,
  fetchFiltersAndTemplates,
  endActiveTemplatesForTopicOrSubtopic,
  getActiveTemplateCount,
  getDisplayedTemplates,
} from './Templates.service';
import TemplateConfirmModal from './TemplateConfirmModal';
import { AlertsContext } from '../shared/alerts/Alerts.context';
import {
  buildTemplateFiltersSearchParams,
  buildTemplatesListSearch,
  parseTemplateFiltersFromSearchParams,
  persistTemplatesListSearch,
  templateFiltersEqual,
} from './templateFilters.utils';

const SEARCH_URL_SYNC_DELAY_MS = 300;

export default function Templates() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState(() =>
    parseTemplateFiltersFromSearchParams(searchParams)
  );
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [templatesToEnd, setTemplatesToEnd] = useState({});
  const [topicDropdownOpen, setTopicDropdownOpen] = useState(false);
  const [subtopicDropdownOpen, setSubtopicDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState(
    () => searchParams.get('search') || ''
  );
  const postTypes = [
    { label: 'Pre-written', value: 'pw' },
    { label: 'Fill-in-the-Blank', value: 'fitb' },
    { label: 'Product Link', value: 'pl' },
  ];
  const [filterValues, setFilterValues] = useState({ postTypes });

  const { showAlert } = useContext(AlertsContext);

  const activeCounts = useMemo(() => {
    if (!filterValues.topics) return {};
    const counts = {};
    filterValues.topics.forEach((topic) => {
      counts[`topic-${topic.id}`] = getActiveTemplateCount(
        templates,
        topic.id,
        'topic'
      );
      topic.subtopics?.forEach((subtopic) => {
        counts[`subtopic-${subtopic.id}`] = getActiveTemplateCount(
          templates,
          subtopic.id,
          'subtopic'
        );
      });
    });
    return counts;
  }, [templates, filterValues.topics]);

  const displayedTemplates = useMemo(
    () => getDisplayedTemplates(templates, appliedFilters, searchQuery),
    [templates, appliedFilters, searchQuery]
  );

  const syncFiltersToUrl = useCallback(
    (filters, query) => {
      const nextParams = buildTemplateFiltersSearchParams(filters, query);
      const nextSearch = nextParams.toString();

      if (nextSearch !== searchParams.toString()) {
        setSearchParams(nextParams, { replace: true });
      }

      persistTemplatesListSearch(buildTemplatesListSearch(filters, query));
    },
    [searchParams, setSearchParams]
  );

  const debouncedSyncFiltersToUrl = useMemo(
    () =>
      debounce(
        (filters, query) => syncFiltersToUrl(filters, query),
        SEARCH_URL_SYNC_DELAY_MS
      ),
    [syncFiltersToUrl]
  );

  useEffect(() => {
    const filters = parseTemplateFiltersFromSearchParams(searchParams);
    const query = searchParams.get('search') || '';

    setAppliedFilters((current) =>
      templateFiltersEqual(current, filters) ? current : filters
    );
    setSearchQuery((current) => (current === query ? current : query));
  }, [searchParams]);

  useEffect(() => {
    fetchFiltersAndTemplatesData();
  }, []);

  useEffect(
    () => () => {
      debouncedSyncFiltersToUrl.cancel();
    },
    [debouncedSyncFiltersToUrl]
  );

  const fetchFiltersAndTemplatesData = async () => {
    setLoadingTemplates(true);
    try {
      const { topics: fetchedTopics, templates: fetchedTemplates } =
        await fetchFiltersAndTemplates();
      setFilterValues((prev) => ({ ...prev, topics: fetchedTopics }));
      setTemplates(fetchedTemplates);
    } catch (error) {
      console.error(error);
      showAlert(
        'Failed to load templates and topics. Please try again.',
        'error'
      );
    } finally {
      setLoadingTemplates(false);
    }
  };

  const handleFilterChange = (query, newFilters = appliedFilters) => {
    const filtersToProcess = { ...newFilters };
    if (filtersToProcess.postType === 'pl') {
      filtersToProcess.topic = '';
      filtersToProcess.subtopic = '';
    }

    const filtersChanged = !templateFiltersEqual(
      filtersToProcess,
      appliedFilters
    );
    const searchChanged = query !== searchQuery;

    setAppliedFilters(filtersToProcess);
    setSearchQuery(query);

    if (filtersChanged) {
      debouncedSyncFiltersToUrl.cancel();
      syncFiltersToUrl(filtersToProcess, query);
      return;
    }

    if (searchChanged) {
      debouncedSyncFiltersToUrl(filtersToProcess, query);
    }
  };

  const navigateToTemplateForm = (pathname) => {
    debouncedSyncFiltersToUrl.cancel();
    syncFiltersToUrl(appliedFilters, searchQuery);

    const listSearch = buildTemplatesListSearch(appliedFilters, searchQuery);
    const isCreate = pathname.endsWith('/new');

    persistTemplatesListSearch(listSearch);
    navigate({
      pathname,
      search: isCreate ? listSearch : '',
      state: {
        templatesListSearch: listSearch,
      },
    });
  };

  const handleDeleteTopic = (topicId, event) => {
    event.stopPropagation();
    if (!topicId) return;
    const activeCount = getActiveTemplateCount(templates, topicId, 'topic');
    const subtopics =
      filterValues.topics
        ?.find((t) => t.id === topicId)
        ?.subtopics?.map((sub) => ({
          name: sub.name,
          qty: getActiveTemplateCount(templates, sub.id, 'subtopic'),
        })) || [];
    let topicName = null;
    const foundTopic = filterValues.topics?.find((t) => t.id === topicId);
    if (foundTopic) topicName = foundTopic.name;
    setTemplatesToEnd({
      id: topicId,
      type: 'topic',
      qty: activeCount,
      name: topicName,
      subtopics,
    });
    setConfirmModalOpen(true);
  };

  const handleDeleteSubtopic = (subtopicId, event) => {
    if (!subtopicId) return;
    event.stopPropagation();
    const activeCount = getActiveTemplateCount(
      templates,
      subtopicId,
      'subtopic'
    );
    let subtopicName = null;
    filterValues.topics?.forEach((topic) => {
      const sub = topic.subtopics?.find((s) => s.id === subtopicId);
      if (sub) subtopicName = sub.name;
    });
    setTemplatesToEnd({
      id: subtopicId,
      type: 'subtopic',
      qty: activeCount,
      name: subtopicName,
    });
    setConfirmModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await endActiveTemplatesForTopicOrSubtopic(
        templates,
        templatesToEnd.id,
        templatesToEnd.type
      );
      showAlert('Templates ended successfully!', 'success');
      setTopicDropdownOpen(false);
      setSubtopicDropdownOpen(false);
      fetchFiltersAndTemplatesData();
    } catch (error) {
      console.error('Failed to end templates:', error);
      showAlert('Failed to end templates. Please try again.', 'error');
    } finally {
      setConfirmModalOpen(false);
      setTemplatesToEnd({});
    }
  };

  return (
    <Stack spacing={4} paddingTop={3}>
      <Stack direction="row" justifyContent="space-between">
        <Typography variant="h4">Templates</Typography>
        <Button
          variant="contained"
          color="primary"
          size="small"
          startIcon={<Add />}
          disabled={!filterValues.topics}
          style={styles.createButton}
          onClick={() => navigateToTemplateForm('/templates/new')}
        >
          Create Template
        </Button>
      </Stack>
      {confirmModalOpen && (
        <TemplateConfirmModal
          templatesData={templatesToEnd}
          isOpen={confirmModalOpen}
          closeModal={() => setConfirmModalOpen(false)}
          onConfirm={handleConfirmDelete}
        />
      )}
      <TemplatesFilters
        filterValues={filterValues}
        appliedFilters={appliedFilters}
        searchQuery={searchQuery}
        handleDeleteTopic={handleDeleteTopic}
        handleDeleteSubtopic={handleDeleteSubtopic}
        onFilterChange={handleFilterChange}
        topicDropdownOpen={topicDropdownOpen}
        setTopicDropdownOpen={setTopicDropdownOpen}
        subtopicDropdownOpen={subtopicDropdownOpen}
        setSubtopicDropdownOpen={setSubtopicDropdownOpen}
        activeCounts={activeCounts}
      />
      {loadingTemplates ? (
        <Box display="flex" justifyContent="center">
          <SpinningSpark isSpinning />
        </Box>
      ) : (
        <Box sx={styles.templatesContainer}>
          {displayedTemplates.map((template) => (
            <TemplateItem
              key={template.id}
              template={template}
              onClick={() =>
                navigateToTemplateForm(`/templates/${template.id}`)
              }
            />
          ))}
        </Box>
      )}
    </Stack>
  );
}

const styles = {
  createButton: {
    backgroundColor: styleColors.base.walmart_blue,
    height: '50px',
    color: 'white',
    padding: '30px auto',
  },
  templatesContainer: {
    display: 'grid',
    gap: '20px',
    gridTemplateColumns: {
      xs: 'repeat(1, 1fr)',
      lg: 'repeat(2, 1fr)',
      xl: 'repeat(3, 1fr)',
    },
  },
};
